import { isNil } from 'lodash';
import { forwardRef, type CSSProperties, type HTMLAttributes } from 'react';

import { cn } from '@ui/lib/utils';

const CENTERED_MIN_WIDTH = 1100;
const CENTERED_FALLBACK_SIDEBAR_WIDTH = 100;
const CENTERED_SIDE_PADDING = 24;
const CENTERED_TOP_PADDING = 30;

interface DivProps extends HTMLAttributes<HTMLDivElement> {
  grid?: boolean;
  row?: boolean;
  column?: boolean;
  gridTemplateColumns?: number;
  centered?: boolean;
  fullHeight?: boolean;
}

const buildGridStyle = (
  grid: boolean | undefined,
  gridTemplateColumns: number | undefined,
  style: CSSProperties | undefined
): CSSProperties => {
  if (grid && gridTemplateColumns) {
    return { gridTemplateColumns: `repeat(${gridTemplateColumns}, 1fr)`, ...style };
  }
  return { ...style };
};

const buildCenteredStyle = (centered: boolean | undefined): CSSProperties => {
  if (!centered) {
    return {};
  }
  const sidebarWidth = `var(--sidebar-width, ${CENTERED_FALLBACK_SIDEBAR_WIDTH}px)`;
  return {
    minWidth: CENTERED_MIN_WIDTH,
    marginInline: 'auto',
    paddingTop: CENTERED_TOP_PADDING,
    paddingLeft: `calc(${sidebarWidth} + ${CENTERED_SIDE_PADDING}px)`,
    paddingRight: CENTERED_SIDE_PADDING,
  };
};

export const Div = forwardRef<HTMLDivElement, DivProps>(
  ({ className, grid, row, column, gridTemplateColumns, centered, fullHeight, style, ...props }, ref) => {
    const gridStyle = buildGridStyle(grid, gridTemplateColumns, style);
    const centeredStyle = buildCenteredStyle(centered);

    return (
      <div
        ref={ref}
        data-slot="div"
        className={cn(
          grid && 'grid',
          row && 'flex flex-row',
          column && 'flex flex-col',
          fullHeight && 'h-screen min-h-0',
          !isNil(props.onClick) && 'cursor-pointer',
          className
        )}
        style={{ ...centeredStyle, ...gridStyle }}
        {...props}
      />
    );
  }
);

Div.displayName = 'Div';
